import { useState, type FormEvent } from 'react'
import { usePage } from '@inertiajs/react'
import type { SharedProps } from '@/types'
import { useTurnstile } from '@/lib/turnstile'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

export default function NewsletterSignup() {
  const { turnstile_site_key: turnstileSiteKey } = usePage<SharedProps>().props
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [website, setWebsite] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const { containerRef, getToken, reset } = useTurnstile(turnstileSiteKey)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setStatus('loading')

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    try {
      const turnstileToken = await getToken()
      const res = await fetch('/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({
          email,
          first_name: firstName,
          last_name: lastName,
          website,
          turnstile_token: turnstileToken,
        }),
      })

      setStatus(res.ok ? 'success' : 'error')
    } catch {
      setStatus('error')
    } finally {
      reset()
    }
  }

  if (status === 'success') {
    return (
      <div className="border-b border-black/5 pb-10 mb-10 text-center">
        <h3 className="font-serif text-2xl font-semibold tracking-tight">You're subscribed!</h3>
        <p className="mt-2 text-base text-muted-foreground">
          We'll keep you updated on upcoming galas and charitable events.
        </p>
      </div>
    )
  }

  return (
    <div className="border-b border-black/5 pb-10 mb-10">
      <div className="text-center max-w-2xl mx-auto">
        <h3 className="font-serif text-2xl font-semibold tracking-tight">
          Join The Gala Season Email List
        </h3>
        <p className="mt-2 text-base text-muted-foreground">
          Stay updated on upcoming galas and charitable events in your area.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-3 max-w-xl mx-auto">
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
        />
        <div ref={containerRef} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            aria-label="First name"
            className="bg-white"
          />
          <Input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            aria-label="Last name"
            className="bg-white"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            aria-label="Email address"
            className="bg-white"
          />
          <Button type="submit" disabled={status === 'loading'} className="shrink-0">
            {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </div>
      </form>

      {status === 'error' && (
        <p className="mt-3 text-center text-base text-red-600">
          Something went wrong. Please try again.
        </p>
      )}
    </div>
  )
}
